import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

/**
 * Tracking SPA pageview pour Google Analytics 4.
 *
 * Sans ce hook, gtag('config') n'est appelé qu'au load initial → tous les changements
 * de route React Router sont invisibles dans GA. Avec ce hook, chaque navigation fire
 * un event `page_view` avec le path, la URL complète, et la langue active comme custom
 * parameter (mappé sur la Custom Dimension "Language" à créer côté GA4 Admin).
 *
 * Skips :
 * - `window.gtag` absent (consent cookies pas accepté, GA pas encore chargé)
 * - Path inchangé (StrictMode mount, etc.)
 *
 * À appeler une seule fois dans App.jsx, à l'intérieur du <Router>.
 */
export function useGaPageView() {
    const { pathname, search } = useLocation();
    const { language } = useLanguage();
    const lastPathRef = useRef(null);

    useEffect(() => {
        if (typeof window.gtag !== 'function') return;

        const fullPath = pathname + search;
        // Évite les double-fires (StrictMode mount, ou re-render même path)
        if (lastPathRef.current === fullPath) return;
        lastPathRef.current = fullPath;

        window.gtag('event', 'page_view', {
            page_path: fullPath,
            page_location: window.location.href,
            page_title: document.title,
            language: language, // Custom Dimension "Language" (Event scope, parameter = language)
        });
    }, [pathname, search, language]);
}

/**
 * Composant zero-render qui active le tracking SPA. À placer dans <Router>.
 */
export default function GaPageView() {
    useGaPageView();
    return null;
}
